import { GridRow } from '../../../types/types';
import { resolveOptionalNumber } from './resolveOptionalNumber';
import { ReportTaxBreakdownBlock, ReportTaxBreakdownRow } from '../types';

const ROUNDING_SCALE = 100;

/** Rounds to two decimals without the binary-float drift of toFixed chains. */
const roundToCents = (value: number): number =>
  Math.round(value * ROUNDING_SCALE) / ROUNDING_SCALE;

/**
 * VAT breakdown grouped by rate.
 *
 * Tax is derived as Σ gross − Σ net per rate, never as a sum of per-item tax:
 * per-item tax is rounded to cents, and summing those roundings drifts from the
 * tax the POS printed on the document. Rows are expected to be the complete
 * result set (see computeAggregate).
 */
export const computeTaxBreakdown = (
  rows: GridRow[],
  block: ReportTaxBreakdownBlock
): { rows: ReportTaxBreakdownRow[]; total: ReportTaxBreakdownRow } => {
  const groups = new Map<string, ReportTaxBreakdownRow>();

  rows.forEach((row) => {
    const net = resolveOptionalNumber(row[block.net]);
    const gross = resolveOptionalNumber(row[block.gross]);

    if (net === undefined && gross === undefined) {
      return;
    }

    const rate = resolveOptionalNumber(row[block.groupBy]);
    // Rows without a readable rate are kept in their own group instead of being
    // dropped: a missing rate must stay visible, not silently vanish from the total.
    const key = rate === undefined ? '' : String(rate);
    const group = groups.get(key) ?? {
      rate: rate === undefined ? null : rate,
      net: 0,
      tax: 0,
      gross: 0,
    };

    group.net += net ?? 0;
    group.gross += gross ?? 0;
    groups.set(key, group);
  });

  const breakdownRows = Array.from(groups.values())
    .map((group) => ({
      rate: group.rate,
      net: roundToCents(group.net),
      gross: roundToCents(group.gross),
      tax: roundToCents(group.gross - group.net),
    }))
    .sort((left, right) => (left.rate ?? -1) - (right.rate ?? -1));

  const total = breakdownRows.reduce<ReportTaxBreakdownRow>(
    (sum, current) => ({
      rate: null,
      net: roundToCents(sum.net + current.net),
      tax: roundToCents(sum.tax + current.tax),
      gross: roundToCents(sum.gross + current.gross),
    }),
    { rate: null, net: 0, tax: 0, gross: 0 }
  );

  return { rows: breakdownRows, total };
};
